import { useState, useEffect, useRef } from "react";
import {
  Drawer,
  DrawerContent,
  DrawerHeader,
  DrawerTitle,
} from "@/components/ui/drawer";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { useToast } from "@/hooks/use-toast";
import { useAuth } from "@/hooks/useAuth";
import { useIsMobile } from "@/hooks/use-mobile";
import { supabase } from "@/integrations/supabase/client";
import { useQueryClient } from "@tanstack/react-query";
import {
  IndianRupee,
  Loader2,
  Check,
  Banknote,
  Smartphone,
  CreditCard,
  Search,
  User,
  AlertCircle,
  ArrowLeft,
} from "lucide-react";
import { cn } from "@/lib/utils";

interface QuickReceivePaymentModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSuccess: () => void;
}

interface Customer {
  id: string;
  name: string;
  mobile: string;
  total_due: number;
}

const paymentModes = [
  { id: "cash", label: "Cash", icon: Banknote, shortcut: "1" },
  { id: "upi", label: "UPI", icon: Smartphone, shortcut: "2" },
  { id: "card", label: "Card", icon: CreditCard, shortcut: "3" },
];

export function QuickReceivePaymentModal({ open, onOpenChange, onSuccess }: QuickReceivePaymentModalProps) {
  const isMobile = useIsMobile();
  const { toast } = useToast();
  const { tenant, refreshSession, setBillingInProgress, isSessionReady, ensureSession } = useAuth();
  const queryClient = useQueryClient();
  const searchInputRef = useRef<HTMLInputElement>(null);
  const amountInputRef = useRef<HTMLInputElement>(null);

  const [searchQuery, setSearchQuery] = useState("");
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [selectedCustomer, setSelectedCustomer] = useState<Customer | null>(null);
  const [isSearching, setIsSearching] = useState(false);
  const [amount, setAmount] = useState("");
  const [paymentMode, setPaymentMode] = useState("cash");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [dueSales, setDueSales] = useState<any[]>([]);

  // Auto-focus on open
  useEffect(() => {
    if (open) {
      setTimeout(() => {
        if (!selectedCustomer) {
          searchInputRef.current?.focus();
        } else {
          amountInputRef.current?.focus();
        }
      }, 100);
    }
  }, [open, selectedCustomer]);

  // Focus amount input when customer is selected
  useEffect(() => {
    if (selectedCustomer) {
      setTimeout(() => {
        amountInputRef.current?.focus();
      }, 100);
    }
  }, [selectedCustomer]);

  // Keyboard shortcuts
  useEffect(() => {
    if (!open) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      // ESC to close or go back
      if (e.key === "Escape") {
        e.preventDefault();
        if (selectedCustomer) {
          setSelectedCustomer(null);
        } else {
          handleClose();
        }
        return;
      }

      // Ctrl+Enter to submit
      if (e.key === "Enter" && e.ctrlKey && selectedCustomer && amount) {
        e.preventDefault();
        handleSubmit();
        return;
      }

      // Alt+1/2/3 for payment modes
      if (e.altKey && ["1", "2", "3"].includes(e.key) && selectedCustomer) {
        e.preventDefault();
        const modeIndex = parseInt(e.key) - 1;
        if (paymentModes[modeIndex]) {
          setPaymentMode(paymentModes[modeIndex].id);
        }
        return;
      }

      // Backspace to go back to search when on customer view
      if (e.key === "Backspace" && selectedCustomer && !amount) {
        e.preventDefault();
        setSelectedCustomer(null);
        return;
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [open, selectedCustomer, amount, isSubmitting]);

  // Search customers with dues
  useEffect(() => {
    if (!open || !tenant?.id || searchQuery.length < 2) {
      setCustomers([]);
      return;
    }

    const searchCustomers = async () => {
      setIsSearching(true);
      try {
        const { data, error } = await supabase
          .from("customers")
          .select("id, name, mobile, total_due")
          .eq("tenant_id", tenant.id)
          .gt("total_due", 0)
          .or(`name.ilike.%${searchQuery}%,mobile.ilike.%${searchQuery}%`)
          .limit(10);

        if (error) throw error;
        setCustomers(data || []);
      } catch (error) {
        console.error("Error searching customers:", error);
      } finally {
        setIsSearching(false);
      }
    };

    const debounce = setTimeout(searchCustomers, 300);
    return () => clearTimeout(debounce);
  }, [searchQuery, open, tenant?.id]);

  // Load due sales for selected customer
  useEffect(() => {
    if (!selectedCustomer) {
      setDueSales([]);
      return;
    }

    const loadDueSales = async () => {
      const { data } = await supabase
        .from("sales")
        .select("id, invoice_number, due_amount, total_amount, created_at")
        .eq("customer_id", selectedCustomer.id)
        .gt("due_amount", 0)
        .order("created_at", { ascending: true });
      setDueSales(data || []);
    };
    loadDueSales();
  }, [selectedCustomer]);

  const resetForm = () => {
    setSearchQuery("");
    setCustomers([]);
    setSelectedCustomer(null);
    setAmount("");
    setPaymentMode("cash");
    setDueSales([]);
  };

  const handleClose = () => {
    resetForm();
    onOpenChange(false);
  };

  const handleSubmit = async () => {
    // CRITICAL: Action-level safety check - be more descriptive
    if (!isSessionReady) {
      // Check what's actually missing
      if (!tenant?.id) {
        toast({ title: "Setup Required", description: "Your workspace is not configured. Please contact support.", variant: "destructive" });
        return;
      }
      
      // If we have tenant but session not "ready", still try to proceed
      console.log('[QuickReceivePaymentModal] isSessionReady false but have tenant, proceeding...');
    }
    
    setBillingInProgress(true);
    
    // Final validation - we only really need tenant for this operation
    if (!tenant?.id) {
      toast({ title: "Setup Required", description: "Workspace not configured", variant: "destructive" });
      setBillingInProgress(false);
      return;
    }
    
    if (!selectedCustomer) {
      toast({ title: "Error", description: "Please select a customer", variant: "destructive" });
      setBillingInProgress(false);
      return;
    }

    const paymentAmount = parseFloat(amount);
    if (isNaN(paymentAmount) || paymentAmount <= 0) {
      toast({ title: "Error", description: "Please enter a valid amount", variant: "destructive" });
      setBillingInProgress(false);
      return;
    }

    if (paymentAmount > selectedCustomer.total_due) {
      toast({ title: "Error", description: `Maximum amount is ₹${selectedCustomer.total_due.toLocaleString()}`, variant: "destructive" });
      setBillingInProgress(false);
      return;
    }

    setIsSubmitting(true);
    try {
      let remainingPayment = paymentAmount;

      // Apply payment to oldest dues first (FIFO)
      for (const sale of dueSales) {
        if (remainingPayment <= 0) break;

        const saleDue = Number(sale.due_amount);
        const paymentForThisSale = Math.min(remainingPayment, saleDue);
        const newDue = saleDue - paymentForThisSale;
        const newReceived = Number(sale.total_amount) - newDue;

        await supabase
          .from("sales")
          .update({
            received_amount: newReceived,
            due_amount: newDue,
            payment_status: newDue === 0 ? "paid" : "partial",
          })
          .eq("id", sale.id);

        remainingPayment -= paymentForThisSale;
      }

      // Update customer total_due
      const newTotalDue = selectedCustomer.total_due - paymentAmount;
      await supabase
        .from("customers")
        .update({ total_due: Math.max(0, newTotalDue), updated_at: new Date().toISOString() })
        .eq("id", selectedCustomer.id);

      queryClient.invalidateQueries({ queryKey: ["customers"] });
      queryClient.invalidateQueries({ queryKey: ["sales"] });
      queryClient.invalidateQueries({ queryKey: ["dashboard"] });

      toast({ title: "✅ Payment Received!", description: `₹${paymentAmount.toLocaleString()} from ${selectedCustomer.name}` });
      handleClose();
      onSuccess();
    } catch (error: any) {
      console.error('[QuickReceivePaymentModal] Error:', error);
      if (error.message?.includes('JWT') || error.code === 'PGRST301') {
        const refreshed = await refreshSession();
        if (refreshed) {
          toast({ title: "Session refreshed", description: "Please try again", variant: "default" });
        } else {
          toast({ title: "Session Expired", description: "Please login again", variant: "destructive" });
        }
      } else {
        toast({ title: "Error", description: error.message || "Failed to record payment", variant: "destructive" });
      }
    } finally {
      setIsSubmitting(false);
      setBillingInProgress(false);
    }
  };

  const formatCurrency = (value: number) => `₹${value.toLocaleString('en-IN')}`;

  const content = (
    <div className="space-y-3 sm:space-y-5">
      {/* Customer Search */}
      {!selectedCustomer ? (
        <div className="space-y-3 sm:space-y-4">
          <div className="space-y-2 sm:space-y-3">
            <Label className="text-base sm:text-lg font-bold">Search Customer with Pending Dues</Label>
            <div className="relative">
              <Search className="absolute left-3 sm:left-4 top-1/2 -translate-y-1/2 h-4 w-4 sm:h-5 sm:w-5 text-muted-foreground" />
              <Input
                ref={searchInputRef}
                placeholder="Type name or mobile number..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="h-11 sm:h-14 text-base sm:text-lg pl-10 sm:pl-12"
                autoComplete="off"
              />
              {isSearching && (
                <Loader2 className="absolute right-3 sm:right-4 top-1/2 -translate-y-1/2 h-4 w-4 sm:h-5 sm:w-5 animate-spin text-muted-foreground" />
              )}
            </div>
          </div>

          {/* Search Results */}
          {customers.length > 0 && (
            <div className="space-y-2 sm:space-y-3 max-h-[250px] sm:max-h-[300px] overflow-y-auto">
              {customers.map((customer, index) => (
                <Card
                  key={customer.id}
                  className="p-3 sm:p-4 cursor-pointer hover:bg-accent/50 hover:border-primary/30 transition-all active:scale-[0.99] touch-manipulation"
                  onClick={() => setSelectedCustomer(customer)}
                  tabIndex={0}
                  onKeyDown={(e) => e.key === "Enter" && setSelectedCustomer(customer)}
                >
                  <div className="flex items-center justify-between gap-2">
                    <div className="flex items-center gap-2 sm:gap-4 min-w-0">
                      <div className="h-10 w-10 sm:h-12 sm:w-12 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                        <User className="h-5 w-5 sm:h-6 sm:w-6 text-primary" />
                      </div>
                      <div className="min-w-0">
                        <p className="font-bold text-sm sm:text-lg truncate">{customer.name}</p>
                        <p className="text-xs sm:text-sm text-muted-foreground">{customer.mobile}</p>
                      </div>
                    </div>
                    <div className="text-right flex-shrink-0">
                      <p className="text-xs sm:text-sm text-muted-foreground">Pending</p>
                      <p className="font-black text-base sm:text-xl text-destructive">{formatCurrency(customer.total_due)}</p>
                    </div>
                  </div>
                </Card>
              ))}
            </div>
          )}

          {searchQuery.length >= 2 && !isSearching && customers.length === 0 && (
            <div className="text-center py-8 sm:py-12 text-muted-foreground">
              <AlertCircle className="h-10 w-10 sm:h-12 sm:w-12 mx-auto mb-2 sm:mb-3 opacity-50" />
              <p className="text-base sm:text-lg">No customers with pending dues found</p>
              <p className="text-xs sm:text-sm mt-1">Try a different name or mobile number</p>
            </div>
          )}

          {searchQuery.length < 2 && (
            <div className="text-center py-8 sm:py-12 text-muted-foreground">
              <Search className="h-10 w-10 sm:h-12 sm:w-12 mx-auto mb-2 sm:mb-3 opacity-30" />
              <p className="text-base sm:text-lg">Start typing to search customers</p>
              <p className="text-xs sm:text-sm mt-1">At least 2 characters required</p>
            </div>
          )}
        </div>
      ) : (
        <>
          {/* Back Button */}
          <Button
            variant="ghost"
            className="gap-2 mb-1 sm:mb-2 -ml-2 text-muted-foreground hover:text-foreground h-9 sm:h-10 touch-manipulation"
            onClick={() => setSelectedCustomer(null)}
          >
            <ArrowLeft className="h-4 w-4" />
            <span className="text-sm sm:text-base">Change Customer</span>
            <kbd className="hidden sm:inline-flex ml-2 px-1.5 py-0.5 text-[10px] bg-muted rounded">ESC</kbd>
          </Button>

          {/* Selected Customer Card */}
          <Card className="p-3 sm:p-4 bg-primary/5 border-primary/20">
            <div className="flex items-center gap-3 sm:gap-4">
              <div className="h-11 w-11 sm:h-14 sm:w-14 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                <User className="h-5 w-5 sm:h-7 sm:w-7 text-primary" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="font-black text-lg sm:text-xl truncate">{selectedCustomer.name}</p>
                <p className="text-sm sm:text-base text-muted-foreground">{selectedCustomer.mobile}</p>
              </div>
            </div>
          </Card>

          {/* Due Amount Display - VERY PROMINENT */}
          <Card className="p-4 sm:p-6 bg-destructive/10 border-2 border-destructive/30">
            <div className="text-center">
              <p className="text-sm sm:text-base text-muted-foreground font-medium mb-1 sm:mb-2">Total Pending Amount</p>
              <p className="text-3xl sm:text-5xl font-black text-destructive">{formatCurrency(selectedCustomer.total_due)}</p>
              <Badge variant="secondary" className="mt-2 sm:mt-3 text-xs sm:text-sm">
                {dueSales.length} unpaid invoice{dueSales.length !== 1 ? 's' : ''}
              </Badge>
            </div>
          </Card>

          {/* Amount Input - LARGE & CLEAR */}
          <div className="space-y-2 sm:space-y-3">
            <Label className="text-base sm:text-lg font-bold">Receive Amount</Label>
            <div className="relative">
              <span className="absolute left-4 sm:left-5 top-1/2 -translate-y-1/2 text-2xl sm:text-3xl text-muted-foreground font-bold">₹</span>
              <Input
                ref={amountInputRef}
                type="number"
                inputMode="numeric"
                placeholder="0"
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                className="h-16 sm:h-20 text-3xl sm:text-4xl font-black pl-12 sm:pl-14 text-center"
                max={selectedCustomer.total_due}
                autoComplete="off"
              />
            </div>
            <div className="flex gap-2 sm:gap-3">
              <Button
                variant="outline"
                className="flex-1 h-10 sm:h-12 text-xs sm:text-base font-semibold touch-manipulation"
                onClick={() => setAmount(String(selectedCustomer.total_due))}
              >
                Full: {formatCurrency(selectedCustomer.total_due)}
              </Button>
              <Button
                variant="outline"
                className="flex-1 h-10 sm:h-12 text-xs sm:text-base font-semibold touch-manipulation"
                onClick={() => setAmount(String(Math.round(selectedCustomer.total_due / 2)))}
              >
                Half: {formatCurrency(Math.round(selectedCustomer.total_due / 2))}
              </Button>
            </div>
          </div>

          {/* Payment Mode - Large with shortcuts */}
          <div className="space-y-2 sm:space-y-3">
            <Label className="text-base sm:text-lg font-bold">Payment Mode</Label>
            <div className="flex gap-2 sm:gap-3">
              {paymentModes.map((pm) => (
                <Button
                  key={pm.id}
                  variant={paymentMode === pm.id ? "default" : "outline"}
                  className={cn(
                    "flex-1 h-11 sm:h-14 gap-1 sm:gap-2 text-xs sm:text-base font-semibold transition-all touch-manipulation",
                    paymentMode === pm.id && "ring-2 ring-primary/30"
                  )}
                  onClick={() => setPaymentMode(pm.id)}
                >
                  <pm.icon className="h-4 w-4 sm:h-5 sm:w-5" />
                  <span>{pm.label}</span>
                  <kbd className="hidden sm:inline-flex ml-auto px-1.5 py-0.5 text-[10px] bg-background/30 rounded">Alt+{pm.shortcut}</kbd>
                </Button>
              ))}
            </div>
          </div>

          {/* Submit - VERY PROMINENT */}
          <Button
            size="lg"
            className="w-full h-12 sm:h-16 text-base sm:text-xl font-bold gap-2 sm:gap-3 shadow-lg hover:shadow-xl transition-all mt-2 sm:mt-4 touch-manipulation"
            onClick={handleSubmit}
            disabled={isSubmitting || !amount || parseFloat(amount) <= 0}
          >
            {isSubmitting ? <Loader2 className="h-5 w-5 sm:h-6 sm:w-6 animate-spin" /> : <Check className="h-5 w-5 sm:h-6 sm:w-6" />}
            <span>Receive Payment</span>
            <kbd className="hidden sm:inline-flex px-2 py-1 text-xs bg-background/30 rounded">Ctrl+↵</kbd>
          </Button>
        </>
      )}
    </div>
  );

  if (isMobile) {
    return (
      <Drawer open={open} onOpenChange={handleClose}>
        <DrawerContent className="max-h-[92vh] px-3 pb-6 pt-4">
          <DrawerHeader className="p-0 pb-3">
            <DrawerTitle className="text-xl sm:text-2xl font-black flex items-center gap-2">
              <IndianRupee className="h-5 w-5 sm:h-6 sm:w-6 text-primary" />
              Receive Payment
            </DrawerTitle>
          </DrawerHeader>
          <div className="overflow-y-auto flex-1">
            {content}
          </div>
        </DrawerContent>
      </Drawer>
    );
  }

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="fixed inset-2 sm:inset-auto sm:left-[50%] sm:top-[50%] sm:translate-x-[-50%] sm:translate-y-[-50%] w-[calc(100%-1rem)] sm:w-full sm:max-w-lg max-h-[calc(100%-1rem)] sm:max-h-[90vh] flex flex-col p-4 sm:p-6 overflow-hidden rounded-lg">
        <DialogHeader className="flex-shrink-0">
          <DialogTitle className="text-xl sm:text-2xl font-black flex items-center gap-2">
            <IndianRupee className="h-5 w-5 sm:h-6 sm:w-6 text-primary" />
            Receive Payment
          </DialogTitle>
        </DialogHeader>
        <div className="overflow-y-auto flex-1">
          {content}
        </div>
      </DialogContent>
    </Dialog>
  );
}
